import type { PlanSummary } from '@rheora/enrollment-contract';

import { filterPlansByTdu } from './zone.service';

/**
 * Pure catalogue helpers for the marketing landing page (E-SF08).
 *
 * Both functions derive display data from the SAME public plan list the shelf
 * already fetches (`usePlansQuery`) — no new network call, and (per CLAUDE.md) no
 * business logic in the page component. They are comparison/dedup only: no money
 * arithmetic happens here.
 */

/**
 * The distinct supplier names in the catalogue, case-insensitively de-duplicated
 * and alphabetically ordered for a stable chip row. The FIRST-seen spelling of a
 * supplier wins — the catalogue is the source of truth for casing. Blank supplier
 * names are dropped rather than rendered as empty chips.
 */
export function uniqueSuppliers(plans: readonly PlanSummary[]): string[] {
  const seen = new Map<string, string>();
  for (const plan of plans) {
    const name = plan.supplierName.trim();
    if (name.length === 0) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * The `n` plans with the lowest disclosed energy rate, shown as a "featured"
 * strip on the landing page.
 *
 * This is a SELECTION heuristic (lowest ¢/kWh energy charge), NOT a price claim —
 * the card itself still headlines the §25.475 average price, so nothing here
 * asserts "cheapest bill". Ties break by plan name so the order is deterministic.
 * A non-numeric rate sorts last rather than throwing. Comparison only — no float
 * money math.
 *
 * ── ⚠️ IT MUST NOT OFFER ANOTHER UTILITY'S PLAN (E-SF19 P1) ─────────────────
 * With CenterPoint confirmed on the same screen, the landing strip featured an
 * ONCOR plan, and opening it rendered full detail with "Continue to approval"
 * live — a plan the customer's meter can never be enrolled on, presented as
 * theirs to buy. `/shop/plans` filtered correctly all along ("Showing CenterPoint
 * Energy plans"); this surface simply never applied the same rule.
 *
 * So `tduCode` narrows the pool BEFORE the selection heuristic runs. Passing it is
 * the caller's job because only the page knows whether a meter is resolved:
 *
 *   - a resolved meter → its TDU code, and only that utility's plans are featured;
 *   - no meter yet     → `undefined`, and the whole catalogue is eligible. That is
 *     correct rather than a loophole: with no meter there is no wrong utility to
 *     offer, and the strip is doing its marketing job for a visitor who has not
 *     told us where they live.
 *
 * ⚠️ IT FILTERS, IT DOES NOT FALL BACK. If the resolved TDU matches no catalogue
 * plan the strip renders EMPTY. Quietly widening to "any plan" on an empty result
 * is exactly the defect being fixed, arriving through the back door — an empty
 * strip is honest, a wrong-utility strip is not.
 */
export function featuredPlans(
  plans: readonly PlanSummary[],
  n = 3,
  tduCode?: string,
): PlanSummary[] {
  const eligible = tduCode === undefined ? plans : filterPlansByTdu(plans, tduCode);
  const rate = (plan: PlanSummary): number => {
    const parsed = Number(plan.rateCentsPerKwh);
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  };
  return [...eligible]
    .sort((a, b) => rate(a) - rate(b) || a.planName.localeCompare(b.planName))
    .slice(0, Math.max(0, n));
}
